import React, { useState, useEffect } from 'react';
import { TestLogEntry, TestResult } from '../types';
import { queryLogWithAI } from '../services/geminiService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface LogAndChartsTabProps {
  log: TestLogEntry[];
  setLog: React.Dispatch<React.SetStateAction<TestLogEntry[]>>;
  poolVolume: number;
  onRerunAnalysis: (entry: TestLogEntry, targetTab: 'ai_action_plan' | 'action_plan') => void;
  onUpdate: (entry: TestLogEntry) => void;
  onDelete: (id: number) => void;
}

const getStatusColorClass = (param: keyof Omit<TestResult, 'acid_demand_drops' | 'base_demand_drops' | 'temp' | 'tds'>, value: number | null): string => {
  if (value === null) return 'text-slate-400 font-semibold';
  const defaultColor = 'text-white font-semibold';
  switch (param) {
    case 'fc':
      if (value < 2) return 'text-orange-400 font-semibold';
      if (value <= 5) return 'text-green-400 font-semibold';
      return 'text-red-400 font-semibold';
    case 'cc':
      if (value < 0.5) return 'text-green-400 font-semibold';
      return 'text-red-400 font-semibold';
    case 'ph':
      if (value < 7.2) return 'text-orange-400 font-semibold';
      if (value <= 7.8) return 'text-green-400 font-semibold';
      return 'text-red-400 font-semibold';
    case 'ta':
      if (value < 80) return 'text-orange-400 font-semibold';
      if (value <= 120) return 'text-green-400 font-semibold';
      return 'text-red-400 font-semibold';
    case 'ch':
      if (value < 200) return 'text-orange-400 font-semibold';
      if (value <= 400) return 'text-green-400 font-semibold';
      return 'text-red-400 font-semibold';
    case 'cya':
      if (value < 30) return 'text-orange-400 font-semibold';
      if (value <= 50) return 'text-green-400 font-semibold';
      return 'text-red-400 font-semibold';
    case 'bromine':
        if (value < 2) return 'text-orange-400 font-semibold';
        if (value <= 6) return 'text-green-400 font-semibold';
        return 'text-red-400 font-semibold';
    default:
      return defaultColor;
  }
};


const AILogAnalysis: React.FC<{log: TestLogEntry[], poolVolume: number}> = ({ log, poolVolume }) => {
    const [question, setQuestion] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [answer, setAnswer] = useState('');

    const handleAskAI = async (query?: string) => {
        const currentQuestion = query || question;
        if (!currentQuestion || log.length === 0) {
            setError("Please enter a question and make sure you have log entries.");
            return;
        }
        setIsLoading(true);
        setError('');
        setAnswer('');
        try {
            const result = await queryLogWithAI(log, currentQuestion, poolVolume);
            setAnswer(result);
        } catch (e) {
            setError(e instanceof Error ? e.message : "An unknown error occurred.");
        } finally {
            setIsLoading(false);
            if (!query) {
              setQuestion('');
            }
        }
    };

    const examplePrompts = [
        "How was my pH last week?",
        "Summarize my chlorine usage this month.",
        "How much muriatic acid do I need to lower my pH based on the last test?",
        "What are the overall trends in my water chemistry?"
    ];

    return (
        <div className="bg-slate-800 p-4 rounded-lg shadow-md mt-8">
            <h2 className="text-2xl font-bold text-white text-center mb-2">Ask AI About Your Log</h2>
            <p className="text-center text-sm text-slate-400 mb-4">Get insights from your test history, including dosage questions.</p>
            <div className="flex gap-2">
                <input
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="e.g., How was my pH last week?"
                    className="flex-grow bg-slate-700 border border-slate-600 rounded-md p-2 text-white placeholder-slate-400"
                    onKeyDown={(e) => e.key === 'Enter' && handleAskAI()}
                />
                <button onClick={() => handleAskAI()} disabled={isLoading} className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-600">
                    {isLoading ? 'Thinking...' : 'Ask'}
                </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
                {examplePrompts.map(p => (
                    <button key={p} onClick={() => { setQuestion(p); handleAskAI(p); }} className="bg-slate-700 hover:bg-slate-600 text-slate-300 py-1 px-2 rounded-md">
                        "{p}"
                    </button>
                ))}
            </div>
            {error && <div className="mt-4 bg-red-900/50 text-red-300 p-3 rounded-md">{error}</div>}
            {answer && (
                <div className="mt-4 bg-slate-900/50 p-4 rounded-lg space-y-2 whitespace-pre-wrap font-sans">
                    <h4 className="font-bold text-lg text-cyan-400">AI Analysis:</h4>
                    <p className="text-slate-300">{answer}</p>
                </div>
            )}
        </div>
    );
};

const FormInput: React.FC<{label: string, name: keyof TestResult, value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void}> = ({label, name, value, onChange}) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-slate-400">{label}</label>
        <input
            type="text"
            inputMode="decimal"
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
        />
    </div>
);

const EditLogEntryModal: React.FC<{ entry: TestLogEntry, onSave: (updatedData: TestResult) => void, onClose: () => void }> = ({ entry, onSave, onClose }) => {
    const [formData, setFormData] = useState<Record<keyof TestResult, string>>(() => {
        const { id: _id, timestamp: _timestamp, ...rest } = entry;
        const stringifiedData: Partial<Record<keyof TestResult, string>> = {};
        (Object.keys(rest) as Array<keyof TestResult>).forEach(key => {
            stringifiedData[key] = rest[key] == null ? '' : String(rest[key]);
        });
        return stringifiedData as Record<keyof TestResult, string>;
    });

    useEffect(() => {
        const { id: _id, timestamp: _timestamp, ...rest } = entry;
        const stringifiedData: Partial<Record<keyof TestResult, string>> = {};
        (Object.keys(rest) as Array<keyof TestResult>).forEach(key => {
            stringifiedData[key] = rest[key] == null ? '' : String(rest[key]);
        });
        setFormData(stringifiedData as Record<keyof TestResult, string>);
    }, [entry]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const NULLABLE_EDIT_FIELDS = new Set<keyof TestResult>(['fc', 'cc', 'ph', 'ta', 'ch', 'cya', 'bromine']);
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const numericData: Partial<Record<keyof TestResult, number | null>> = {};
        (Object.keys(formData) as Array<keyof TestResult>).forEach(key => {
            const parsedValue = parseFloat(formData[key]);
            numericData[key] = isNaN(parsedValue) ? (NULLABLE_EDIT_FIELDS.has(key) ? null : 0) : parsedValue;
        });
        onSave(numericData as TestResult);
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <form onSubmit={handleSubmit}>
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-white">Edit Log Entry</h2>
                            <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">&times;</button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <FormInput label="Free Chlorine (ppm)" name="fc" value={formData.fc} onChange={handleChange} />
                            <FormInput label="Combined Chlorine (ppm)" name="cc" value={formData.cc} onChange={handleChange} />
                            <FormInput label="pH" name="ph" value={formData.ph} onChange={handleChange} />
                            <FormInput label="Total Alkalinity (ppm)" name="ta" value={formData.ta} onChange={handleChange} />
                            <FormInput label="Calcium Hardness (ppm)" name="ch" value={formData.ch} onChange={handleChange} />
                            <FormInput label="Cyanuric Acid (ppm)" name="cya" value={formData.cya} onChange={handleChange} />
                            <FormInput label="Bromine (ppm)" name="bromine" value={formData.bromine} onChange={handleChange} />
                            <FormInput label="Water Temp (°F)" name="temp" value={formData.temp} onChange={handleChange} />
                            <FormInput label="TDS (ppm)" name="tds" value={formData.tds} onChange={handleChange} />
                            <FormInput label="Acid Demand Drops" name="acid_demand_drops" value={formData.acid_demand_drops} onChange={handleChange} />
                            <FormInput label="Base Demand Drops" name="base_demand_drops" value={formData.base_demand_drops} onChange={handleChange} />
                        </div>
                    </div>
                    <div className="bg-slate-900/50 px-6 py-3 flex justify-end gap-3 rounded-b-lg">
                        <button type="button" onClick={onClose} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-lg">Cancel</button>
                        <button type="submit" className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-2 px-4 rounded-lg">Save Changes</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export const LogAndChartsTab: React.FC<LogAndChartsTabProps> = ({ log, setLog, poolVolume, onRerunAnalysis, onUpdate, onDelete }) => {
  const [editingEntry, setEditingEntry] = useState<TestLogEntry | null>(null);

  const formattedData = log
    .map(entry => ({
      ...entry,
      name: new Date(entry.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    }))
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this log entry?')) {
      onDelete(id);
    }
  };

  const handleSaveEdit = (updatedData: TestResult) => {
    if (!editingEntry) return;
    onUpdate({ ...editingEntry, ...updatedData });
    setEditingEntry(null);
  };

  const handleExport = () => {
    if (log.length === 0) {
        alert("No log entries to export.");
        return;
    }
    const header = ['id', 'timestamp', 'fc', 'cc', 'ph', 'ta', 'ch', 'cya', 'bromine', 'acid_demand_drops', 'base_demand_drops', 'temp', 'tds'];
    const csvRows = [
        header.join(','),
        ...log.map(entry => header.map(fieldName => {
            const val = entry[fieldName as keyof TestLogEntry];
            return val == null ? '' : val;
        }).join(','))
    ];
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `aqua_ai_log_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
        const text = event.target?.result as string;
        if (!text) { alert("Error: Could not read the file."); return; }
        const rows = text.split('\n').map(row => row.trim()).filter(row => row);
        const headerRow = rows[0];
        if (rows.length < 2 || headerRow === undefined) { alert("Error: CSV file is empty or contains only a header."); return; }
        const header = headerRow.split(',').map(h => h.trim());
        const expectedHeader = ['id', 'timestamp', 'fc', 'cc', 'ph', 'ta', 'ch', 'cya', 'bromine', 'acid_demand_drops', 'base_demand_drops', 'temp', 'tds'];
        if(header.length !== expectedHeader.length || !expectedHeader.every((h, i) => h === header[i])) {
             alert(`Error: Invalid CSV header. Expected: ${expectedHeader.join(',')}`);
             return;
        }
        const parseNullableFloat = (s: string): number | null => { const n = parseFloat(s); return isNaN(n) ? null : n; };
        const existingIds = new Set(log.map(entry => entry.id));
        const newEntries: TestLogEntry[] = [];
        let errorCount = 0;
        let duplicateCount = 0;
        for (let i = 1; i < rows.length; i++) {
            const row = rows[i];
            if (row === undefined) { errorCount++; continue; }
            const values = row.split(',');
            if (values.length !== header.length) { errorCount++; continue; }
            const v = (n: number): string => values[n] ?? '';
            try {
                const entry: TestLogEntry = {
                    id: Number(v(0)), timestamp: v(1),
                    fc: parseNullableFloat(v(2)), cc: parseNullableFloat(v(3)), ph: parseNullableFloat(v(4)),
                    ta: parseNullableFloat(v(5)), ch: parseNullableFloat(v(6)), cya: parseNullableFloat(v(7)),
                    bromine: parseNullableFloat(v(8)), acid_demand_drops: parseFloat(v(9)) || 0,
                    base_demand_drops: parseFloat(v(10)) || 0, temp: parseFloat(v(11)) || 0, tds: parseFloat(v(12)) || 0,
                };
                if (isNaN(entry.id) || !entry.timestamp) { errorCount++; continue; }
                if (existingIds.has(entry.id)) { duplicateCount++; } else { newEntries.push(entry); existingIds.add(entry.id); }
            } catch { errorCount++; }
        }
        if (newEntries.length > 0) setLog(prevLog => [...prevLog, ...newEntries]);
        alert(`Import complete.\n- ${newEntries.length} new entries added.\n- ${duplicateCount} duplicate entries skipped.\n- ${errorCount} rows with errors were skipped.`);
        if (e.target) e.target.value = '';
    };
    reader.onerror = () => alert("Error: Failed to read file.");
    reader.readAsText(file);
  };

  const renderCharts = () => {
    if (log.length < 2) {
      return <div className="text-center text-slate-400 py-8 bg-slate-800 rounded-lg"><p>Need at least two saved tests to display charts.</p></div>;
    }
    return (
      <div className="space-y-6">
        <div className="bg-slate-800 p-4 rounded-lg shadow-md">
          <h3 className="font-semibold mb-4 text-center text-white">Chlorine Levels (ppm)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={formattedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
              <Legend />
              <Line type="monotone" dataKey="fc" name="Free Chlorine" stroke="#22d3ee" activeDot={{ r: 8 }} />
              <Line type="monotone" dataKey="cc" name="Combined Chlorine" stroke="#f472b6" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-800 p-4 rounded-lg shadow-md">
                <h3 className="font-semibold mb-4 text-center text-white">pH Level</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={formattedData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="name" stroke="#94a3b8" />
                        <YAxis stroke="#94a3b8" domain={[6.8, 8.2]} />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                        <Legend />
                        <Line type="monotone" dataKey="ph" name="pH" stroke="#a78bfa" activeDot={{ r: 8 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg shadow-md">
                <h3 className="font-semibold mb-4 text-center text-white">Total Alkalinity (ppm)</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={formattedData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="name" stroke="#94a3b8" />
                        <YAxis stroke="#94a3b8" />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                        <Legend />
                        <Line type="monotone" dataKey="ta" name="TA" stroke="#4ade80" activeDot={{ r: 8 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
      </div>
    );
  };

  const renderLog = () => {
    if (log.length === 0) {
        return <div className="text-center text-slate-400 py-8 bg-slate-800 rounded-lg"><p>No saved tests yet.</p></div>;
    }
    const sortedLog = [...log].sort((a,b) => b.id - a.id);
    const buttonClass = "bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-3 rounded-lg transition-colors duration-300 text-sm flex-1 text-center";

    return (
        <div className="space-y-4">
            <div className="flex justify-center items-center gap-4 -mb-2 text-xs text-slate-400 no-print">
                <span className="font-bold">Legend:</span>
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-orange-400"></span><span>Low</span></div>
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-400"></span><span>Good</span></div>
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-400"></span><span>High</span></div>
            </div>
            {sortedLog.map(entry => (
                <div key={entry.id} className="bg-slate-800 p-4 rounded-lg">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="font-bold text-lg text-white">
                                {new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </p>
                        </div>
                        <button onClick={() => handleDelete(entry.id)} className="no-print text-slate-400 hover:text-red-500 transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                        </button>
                    </div>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                        <p><strong className="text-cyan-400">FC:</strong> <span className={getStatusColorClass('fc', entry.fc)}>{entry.fc != null ? `${entry.fc} ppm` : '—'}</span></p>
                        <p><strong className="text-cyan-400">CC:</strong> <span className={getStatusColorClass('cc', entry.cc)}>{entry.cc != null ? `${entry.cc} ppm` : '—'}</span></p>
                        <p><strong className="text-cyan-400">pH:</strong> <span className={getStatusColorClass('ph', entry.ph)}>{entry.ph != null ? entry.ph : '—'}</span></p>
                        <p><strong className="text-cyan-400">TA:</strong> <span className={getStatusColorClass('ta', entry.ta)}>{entry.ta != null ? `${entry.ta} ppm` : '—'}</span></p>
                        <p><strong className="text-cyan-400">CH:</strong> <span className={getStatusColorClass('ch', entry.ch)}>{entry.ch != null ? `${entry.ch} ppm` : '—'}</span></p>
                        <p><strong className="text-cyan-400">CYA:</strong> <span className={getStatusColorClass('cya', entry.cya)}>{entry.cya != null ? `${entry.cya} ppm` : '—'}</span></p>
                        {entry.bromine != null && entry.bromine > 0 && <p><strong className="text-cyan-400">Br:</strong> <span className={getStatusColorClass('bromine', entry.bromine)}>{entry.bromine} ppm</span></p>}
                    </div>
                    <div className="mt-4 border-t border-slate-700 pt-3 flex flex-wrap gap-2">
                        <button onClick={() => onRerunAnalysis(entry, 'ai_action_plan')} className={buttonClass}>View AI Plan</button>
                        <button onClick={() => onRerunAnalysis(entry, 'action_plan')} className={buttonClass}>View Rule-Based Plan</button>
                        <button onClick={() => setEditingEntry(entry)} className={buttonClass}>Edit</button>
                    </div>
                </div>
            ))}
        </div>
    );
  };

  const buttonClasses = "no-print bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300 flex items-center gap-2 text-sm";

  return (
    <div className="printable-content space-y-8 animate-fade-in">
        <div className="bg-slate-800 p-4 rounded-lg shadow-md">
            <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
                <h2 className="text-2xl font-bold text-white w-full text-center sm:w-auto sm:flex-1">Historical Charts</h2>
                <div className="flex justify-center sm:justify-end gap-2 w-full sm:w-auto sm:flex-1">
                    <button onClick={handleExport} className={buttonClasses}>Export CSV</button>
                    <label className={buttonClasses + " cursor-pointer"}>
                        Import CSV
                        <input type="file" accept=".csv" hidden onChange={handleFileImport} />
                    </label>
                    <button onClick={() => window.print()} className={buttonClasses}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
                        Print
                    </button>
                </div>
            </div>
            {renderCharts()}
        </div>

        <AILogAnalysis log={log} poolVolume={poolVolume} />

        <div>
            <h2 className="text-2xl font-bold text-white text-center mb-4">Test Log</h2>
            {renderLog()}
        </div>

        {editingEntry && <EditLogEntryModal entry={editingEntry} onSave={handleSaveEdit} onClose={() => setEditingEntry(null)} />}
    </div>
  );
};
